import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, CheckCircle, Crown } from 'lucide-react';
import SearchableTagSelect from '@/components/common/SearchableTagSelect';

export default function Phase5Closing({ session, sessionIssues, officers, onComplete }) {
  const noiseCount = sessionIssues.filter((i) => i.classification_gate1 === 'noise').length;
  const signalCount = sessionIssues.filter((i) => i.classification_gate1 === 'signal').length;
  const balloonCount = sessionIssues.filter((i) => i.classification_gate2 === 'balloon').length;
  const rockCount = sessionIssues.filter((i) => i.classification_gate2 === 'rock').length;

  // Pre-populate pattern observations from shared tags across rocks
  const rockIssues = sessionIssues.filter((i) => i.classification_gate2 === 'rock');
  const sharedTags = {};
  rockIssues.forEach((issue) => {
    [...(issue.tagged_officers || []), ...(issue.tagged_capabilities || []), ...(issue.tagged_policies || [])].forEach((tag) => {
      sharedTags[tag] = (sharedTags[tag] || 0) + 1;
    });
  });
  const crossCuttingTags = Object.entries(sharedTags).filter(([, c]) => c >= 2).map(([t]) => t);

  const [patternObservations, setPatternObservations] = useState(
    session?.pattern_observations || (crossCuttingTags.length > 0 ?
    `Cross-cutting themes detected across Rock issues: ${crossCuttingTags.join(', ')}` :
    '')
  );
  const [upwardReport, setUpwardReport] = useState(session?.upward_report || '');
  const [actionItems, setActionItems] = useState(session?.action_items || []);
  const [nextSessionDate, setNextSessionDate] = useState(session?.next_session_date?.split('T')[0] || '');
  const [nextSessionPrework, setNextSessionPrework] = useState(session?.next_session_prework || '');

  const officerOptions = officers.map((o) => ({ value: o.user_email, label: `${o.callsign} (${o.rank_title})` }));

  const addActionItem = () => {
    setActionItems([...actionItems, { description: '', owner: '', deadline: '', status: 'pending' }]);
  };

  const updateActionItem = (index, field, value) => {
    const updated = [...actionItems];
    updated[index] = { ...updated[index], [field]: value };
    setActionItems(updated);
  };

  const removeActionItem = (index) => {
    setActionItems(actionItems.filter((_, i) => i !== index));
  };

  const handleComplete = () => {
    onComplete({
      noise_count: noiseCount,
      signal_count: signalCount,
      balloon_count: balloonCount,
      rock_count: rockCount,
      pattern_observations: patternObservations,
      upward_report: upwardReport,
      action_items: actionItems,
      next_session_date: nextSessionDate ? new Date(nextSessionDate).toISOString() : null,
      next_session_prework: nextSessionPrework
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h2 className="text-xl font-bold text-white">Phase 5: Closing — Synthesis</h2>
        <p className="text-sm text-slate-300">5-10 minutes — Capture insights and plan next steps</p>
      </div>

      {/* Auto Summary */}
      <Card className="glass-panel border-emerald-500/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-emerald-400 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" /> Session Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-800 text-sm">
            This session reviewed <span className="font-bold text-white">{sessionIssues.length}</span> issues: {' '}
            <span className="text-slate-200">{noiseCount}</span> classified as Noise, {' '}
            <span className="text-amber-300">{signalCount}</span> as Signal. {' '}
            Of {signalCount} signals: <span className="text-sky-300">🎈 {balloonCount} Balloons</span>, {' '}
            <span className="text-stone-500">🪨 {rockCount} Rocks</span>.
          </p>
        </CardContent>
      </Card>

      {/* Pattern Observations */}
      <div className="space-y-2">
        <Label className="text-slate-300">Pattern Observations</Label>
        <p className="text-xs text-slate-400">Are there systemic themes emerging across issues?</p>
        <Textarea data-testid="hc-phase5closing-textarea"
          value={patternObservations}
          onChange={(e) => setPatternObservations(e.target.value)}
          className="bg-slate-800/60 border-slate-700 text-slate-200"
          rows={3}
          placeholder="Describe any patterns or systemic themes..." />

      </div>

      {/* Upward Report */}
      <div className="space-y-2">
        <Label className="text-slate-300">Upward Report</Label>
        <p className="text-xs text-slate-400">Key findings that need to flow up the framework (Tactics → Strategy → Direction)</p>
        <Textarea data-testid="hc-phase5closing-textarea-1"
          value={upwardReport}
          onChange={(e) => setUpwardReport(e.target.value)}
          className="bg-slate-800/60 border-slate-700 text-slate-200"
          rows={3}
          placeholder="What needs escalation to strategic leadership?" />

      </div>

      {/* Action Items */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-slate-300">Action Items</Label>
          <Button data-testid="hc-add-btn" size="sm" variant="outline" onClick={addActionItem} className="bg-background text-slate-700 px-3 text-xs font-medium rounded-md inline-flex items-center justify-center gap-2 whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border shadow-sm hover:bg-accent hover:text-accent-foreground h-7 border-slate-600">
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
        {actionItems.map((item, i) =>
        <div key={i} className="grid grid-cols-12 gap-2 items-start p-3 rounded-lg bg-slate-800/40 border border-slate-700">
            <div className="col-span-4 space-y-1">
              <Label className="text-[10px] text-slate-400">Description</Label>
              <Input data-testid="hc-text-10px-text-slate-400-input"
              value={item.description}
              onChange={(e) => updateActionItem(i, 'description', e.target.value)}
              className="bg-slate-950 border-slate-700 h-8 text-sm"
              placeholder="What needs to be done?" />

            </div>
            <div className="col-span-3 space-y-1">
              <Label className="text-[10px] text-slate-400">Owner</Label>
              <SearchableTagSelect
              options={officerOptions}
              value={item.owner}
              onValueChange={(v) => updateActionItem(i, 'owner', v)}
              placeholder="Assign..."
              emptyMessage="No officers." />

            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-[10px] text-slate-400">Deadline</Label>
              <Input data-testid="hc-text-10px-text-slate-400-input-1"
              type="date"
              value={item.deadline}
              onChange={(e) => updateActionItem(i, 'deadline', e.target.value)}
              className="bg-slate-950 border-slate-700 h-8 text-sm" />

            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-[10px] text-slate-400">Status</Label>
              <Select value={item.status || 'pending'} onValueChange={(v) => updateActionItem(i, 'status', v)}>
                <SelectTrigger className="bg-slate-950 border-slate-700 h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-1 flex items-end justify-center pb-0.5">
              <Button size="icon" variant="ghost" className="h-8 w-8 hover:text-red-400" onClick={() => removeActionItem(i)}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Next Session */}
      <Card className="glass-panel border-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-indigo-400">Next Session</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-slate-700 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Date</Label>
            <Input data-testid="hc-text-slate-700-text-xs-font-medium-peer-input"
              type="date"
              value={nextSessionDate}
              onChange={(e) => setNextSessionDate(e.target.value)} className="bg-slate-50 text-slate-700 px-3 py-1 text-base rounded-md flex h-9 w-full border shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm border-slate-700" />


          </div>
          <div className="space-y-1">
            <Label className="text-slate-700 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Pre-work</Label>
            <Textarea data-testid="hc-phase5closing-textarea-2"
              value={nextSessionPrework}
              onChange={(e) => setNextSessionPrework(e.target.value)} className="bg-slate-50 text-slate-700 px-3 py-2 text-sm rounded-md flex min-h-[60px] w-full border shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm border-slate-700"

              rows={2}
              placeholder="What should attendees prepare?" />

          </div>
        </CardContent>
      </Card>

      <Button data-testid="hc-complete-session-btn" onClick={handleComplete} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 text-base">
        <Crown className="w-5 h-5 mr-2" /> Complete Session
      </Button>
    </div>);

}